// Free geocoding via public APIs (no API key needed). The host page's CSP
// must allow these origins — they're already in the POC's connect-src list:
//   https://api.zippopotam.us — postcode → city + lat/long (US, GB, CA, AU)
//
// For the widget's "type your zip / postcode" entry point we only need the
// postcode → coords path. Reverse-geocoding (for an HTML5 geolocation flow)
// is deferred.

interface ZippopotamResponse {
  'post code': string;
  country: string;
  places: Array<{
    'place name': string;
    state: string;
    'state abbreviation': string;
    longitude: string;
    latitude: string;
  }>;
}

export interface ZipGeocode {
  zip: string;
  city: string;
  state: string;
  stateAbbr: string;
  lat: number;
  lng: number;
}

/**
 * Country-aware postcode acceptance. The widget's "Enter Postcode" input
 * takes the raw user string; the LocationField uses these matchers to
 * decide "should I try geocoding this?" before hitting the API. Loose
 * enough to accept partials (UK outward-only "GU2", CA FSA-only "M5V")
 * because zippopotam's international datasets are keyed at that level.
 */
const POSTAL_MATCHERS: Record<string, RegExp> = {
  us: /^\d{5}$/,
  // UK: allow outward code alone ("SW1A", "GU2") OR full postcode with
  // optional space ("SW1A 1AA", "GU2 4QR"). Case-insensitive.
  uk: /^[A-Z]{1,2}\d[A-Z\d]?(\s?\d[A-Z]{2})?$/i,
  // CA: allow forward sortation area alone ("M5V") OR full postcode
  // with optional space ("M5V 3A8"). Case-insensitive.
  ca: /^[A-Z]\d[A-Z](\s?\d[A-Z]\d)?$/i,
  // AU: 4-digit numeric.
  au: /^\d{4}$/,
};

/** Which zippopotam.us country prefix to use per widget country code. */
const ZIPPO_COUNTRY: Record<string, string> = { us: 'us', uk: 'gb', ca: 'ca', au: 'au' };

/**
 * Return true iff the input looks like a valid postcode in the given country.
 * Callers use this to decide whether to fire the geocode request or fall
 * through to name/address substring search.
 */
export function looksLikePostcode(input: string, country: string | null | undefined): boolean {
  const c = (country ?? 'us').trim().toLowerCase();
  const re = POSTAL_MATCHERS[c];
  return re ? re.test(input.trim()) : false;
}

/**
 * Normalise the input into the form zippopotam.us actually accepts —
 * outward-code-only for UK, forward-sortation-area for CA. US/AU are
 * unchanged. Case is upper for UK/CA; US/AU stay as-typed.
 */
function normalisePostcode(input: string, country: string): string {
  const trimmed = input.trim();
  switch (country) {
    case 'uk': {
      // Uppercase, drop any inward code (everything from the first space
      // onward) so "GU2 4QR" → "GU2" and "SW1A 1AA" → "SW1A".
      const upper = trimmed.toUpperCase().replace(/\s+/, ' ');
      return upper.split(' ')[0];
    }
    case 'ca': {
      // Uppercase, drop any inward code so "M5V 3A8" → "M5V".
      const upper = trimmed.toUpperCase().replace(/\s+/, ' ');
      return upper.split(' ')[0];
    }
    default:
      return trimmed;
  }
}

/**
 * Geocode a postcode against zippopotam.us. `country` selects the dataset;
 * defaults to 'us' so existing callers stay backwards compatible with the
 * previous US-only signature. Returns null when the input doesn't look
 * like a postcode for that country, when zippopotam has no record, or
 * on any network failure.
 */
export async function geocodeZip(
  zip: string,
  country: string | null | undefined = 'us',
): Promise<ZipGeocode | null> {
  const c = (country ?? 'us').trim().toLowerCase();
  const zippoCountry = ZIPPO_COUNTRY[c];
  if (!zippoCountry) return null;

  if (!looksLikePostcode(zip, c)) return null;
  const clean = normalisePostcode(zip, c);

  try {
    const res = await fetch(`https://api.zippopotam.us/${zippoCountry}/${encodeURIComponent(clean)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as ZippopotamResponse;
    const place = data.places?.[0];
    if (!place) return null;
    return {
      zip: data['post code'],
      city: place['place name'],
      state: place.state,
      stateAbbr: place['state abbreviation'],
      lat: parseFloat(place.latitude),
      lng: parseFloat(place.longitude),
    };
  } catch {
    return null;
  }
}

// Simple debounce helper for autocomplete-as-you-type
export function debounce<F extends (...args: never[]) => void>(fn: F, ms: number): F {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return ((...args: never[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as F;
}

/**
 * Major metropolitan centres, by market.
 *
 * Store records carry their suburb, not their city: the Sydney studio is
 * recorded as "Alexandria, New South Wales", so a customer typing "Sydney" —
 * the name of the city it is actually in — matched no text and was told there
 * were no stores in a region that has one. Postcodes already resolve through
 * zippopotam; this covers the case where someone names the city instead.
 *
 * Deliberately a short curated list rather than a general place-name geocoder:
 * these are the queries customers actually type, the coordinates do not change,
 * and it costs no network call on a path that runs as the user types.
 */
const METRO_CENTRES: Record<string, Array<{ names: string[]; lat: number; lng: number }>> = {
  au: [
    { names: ['sydney'], lat: -33.8688, lng: 151.2093 },
    { names: ['melbourne'], lat: -37.8136, lng: 144.9631 },
    { names: ['brisbane'], lat: -27.4698, lng: 153.0251 },
    { names: ['adelaide'], lat: -34.9285, lng: 138.6007 },
    { names: ['perth'], lat: -31.9523, lng: 115.8613 },
    { names: ['canberra'], lat: -35.2809, lng: 149.1300 },
  ],
  uk: [
    { names: ['london'], lat: 51.5072, lng: -0.1276 },
    { names: ['glasgow'], lat: 55.8642, lng: -4.2518 },
    { names: ['edinburgh'], lat: 55.9533, lng: -3.1883 },
    { names: ['manchester'], lat: 53.4808, lng: -2.2426 },
    { names: ['birmingham'], lat: 52.4862, lng: -1.8904 },
  ],
  ca: [
    { names: ['toronto'], lat: 43.6532, lng: -79.3832 },
    { names: ['vancouver'], lat: 49.2827, lng: -123.1207 },
    { names: ['montreal', 'montréal'], lat: 45.5019, lng: -73.5674 },
    { names: ['calgary'], lat: 51.0447, lng: -114.0719 },
  ],
};

/**
 * Resolve a typed city name to coordinates, so proximity ranking can answer a
 * query the store's own address does not contain. Returns null for anything
 * not on the list — the caller falls back to text matching as before.
 */
export function geocodeMetro(input: string, country: string | null | undefined): ZipGeocode | null {
  const c = (country ?? 'us').trim().toLowerCase();
  const needle = input.trim().toLowerCase();
  if (!needle) return null;
  const hit = (METRO_CENTRES[c] ?? []).find((m) => m.names.includes(needle));
  if (!hit) return null;
  // Shaped as a ZipGeocode so it drops straight into the existing ranking.
  // City and state are left blank rather than guessed: they drive the
  // same-city / same-state tiers, and a wrong value there would promote the
  // wrong store above a genuinely closer one.
  return { zip: '', city: '', state: '', stateAbbr: '', lat: hit.lat, lng: hit.lng };
}
